"use client";

import { useCallback, useState } from "react";

import { useHubspotContext } from "@/components/forms/hubspot_context";
import {
  NEWSLETTER_FORM_NAME,
  NEWSLETTER_HUBSPOT_FORM_GUID,
} from "@/lib/newsletter/constants";

export interface UseNewsletterSubscribeResult {
  submit: (email: string) => Promise<void>;
  isSubmitting: boolean;
  isSubscribed: boolean;
  hasError: boolean;
}

/**
 * Subscribes an email to the HubSpot newsletter form via the first-party
 * /api/submit-hubspot-form route (proxied server-side to avoid ad-blocker and
 * CORS issues with api.hsforms.com). Unlike the apply-now capture this never
 * redirects — subscribing keeps the reader on the page.
 */
export function useNewsletterSubscribe(): UseNewsletterSubscribeResult {
  const hubspotContext = useHubspotContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [hasError, setHasError] = useState(false);

  const submit = useCallback(
    async (email: string) => {
      const trimmed = email.trim();
      if (!trimmed) return;

      setIsSubmitting(true);
      setHasError(false);
      try {
        const response = await fetch("/api/submit-hubspot-form", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            guid: NEWSLETTER_HUBSPOT_FORM_GUID,
            formName: NEWSLETTER_FORM_NAME,
            data: { email: trimmed },
            hubspotContext: {
              ...hubspotContext,
              pageName: NEWSLETTER_FORM_NAME,
            },
          }),
        });
        if (response.ok) {
          setIsSubscribed(true);
        } else {
          setHasError(true);
        }
      } catch {
        setHasError(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [hubspotContext]
  );

  return { submit, isSubmitting, isSubscribed, hasError };
}
